import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Euro, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { parseApiError } from '../lib/parseApiError';
import { DOCUMENTO_LABELS } from '../lib/documentiConst';
import DocumentoFormDialog from '../components/documenti/DocumentoFormDialog';
import DocumentiFilters from '../components/documenti/DocumentiFilters';
import DocumentiTable from '../components/documenti/DocumentiTable';
import PdfPreviewDialog from '../components/documenti/PdfPreviewDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTRI_VUOTI = { search: '', tipo: '', stato: '', cliente: '', creatoDa: '', dataDa: '', dataA: '' };

export default function DocumentiPage() {
  const { isAdmin, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [documenti, setDocumenti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtri, setFiltri] = useState(FILTRI_VUOTI);
  const [soloNonPagate, setSoloNonPagate] = useState(searchParams.get('nonpagate') === '1');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [filtri, soloNonPagate]);
  const [sortBy, setSortBy] = useState('data');
  const [sortDir, setSortDir] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState(null);
  const [dataFatturaDialog, setDataFatturaDialog] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Tipi documento visibili in base ai permessi utente
  const permessiDocumenti = user?.permessi_documenti || {};
  const tipiVisibili = isAdmin()
    ? ['preventivo', 'offerta', 'ddt', 'fattura', 'nota_credito']
    : ['preventivo', 'offerta',
        ...(permessiDocumenti.ddt ? ['ddt'] : []),
        ...(permessiDocumenti.fattura ? ['fattura'] : []),
        ...(permessiDocumenti.nota_credito ? ['nota_credito'] : [])];

  const fetchData = useCallback(async () => {
    try {
      const [documentiRes, clientiRes, utentiRes] = await Promise.all([
        axios.get(`${API}/documenti`),
        axios.get(`${API}/clienti`),
        isAdmin() ? axios.get(`${API}/users`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }) // Solo admin può vedere utenti
      ]);
      setDocumenti(documentiRes.data);
      setClienti(clientiRes.data);
      setUtenti(utentiRes.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente?.ragione_sociale || '-';
  };

  // ---- Azioni documento ----
  const canEditDoc = (doc) =>
    isAdmin() ||
    (user?.ruolo === 'commerciale' && ['preventivo', 'offerta'].includes(doc.tipo) && doc.created_by === user.id);

  const handleTogglePagato = async (doc, pagato) => {
    try {
      await axios.patch(`${API}/documenti/${doc.id}/pagato`, { pagato });
      toast.success(pagato ? `Fattura #${doc.numero} segnata come PAGATA` : `Fattura #${doc.numero} segnata come da pagare`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiornamento pagamento');
    }
  };

  const handleNotaCredito = async (doc) => {
    const anno = (doc.data_documento || doc.created_at || '').slice(0, 4);
    const tot = (doc.importo_totale || 0).toFixed(2);
    if (!window.confirm(`Creare la NOTA DI CREDITO per la fattura n. ${anno}/${String(doc.numero).padStart(3, '0')} di € ${tot}?\n\nVerrà creata con importo negativo (storno integrale) e riferimento alla fattura.`)) return;
    try {
      const res = await axios.post(`${API}/documenti/${doc.id}/nota-credito`);
      const nc = res.data;
      toast.success(`✅ Nota di credito n. ${(nc.created_at || '').slice(0, 4)}/${String(nc.numero).padStart(3, '0')} creata (€ ${nc.importo_totale.toFixed(2)})`);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore creazione nota di credito'));
    }
  };

  const handleEdit = async (documento) => {
    try {
      const res = await axios.get(`${API}/documenti/${documento.id}`);
      setEditingDocumento(res.data);
      setDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento documento');
    }
  };

  const handleNuovo = () => {
    setEditingDocumento(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API}/documenti/${id}`);
      toast.success('Documento eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const downloadBlob = (data, filename, type) => {
    const url = window.URL.createObjectURL(new Blob([data], type ? { type } : undefined));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadXML = async (id, tipo, numero) => {
    try {
      const response = await axios.get(`${API}/documenti/${id}/xml`, { responseType: 'blob' });
      // Nome file dal Content-Disposition (IT{piva}_{progressivo}.xml)
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `${tipo}_${numero}.xml`;
      downloadBlob(response.data, filename, 'application/xml');
      toast.success(`✅ XML FatturaPA scaricato: ${filename} — caricalo su Aruba o sul portale SdI`);
    } catch (error) {
      // L'errore arriva come blob: convertiamolo per leggere il dettaglio
      let msg = 'Errore generazione XML';
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.detail) msg = parsed.detail;
        }
      } catch (parseErr) { console.error('Parse errore blob fallito:', parseErr); }
      toast.error(msg, { duration: 10000 });
    }
  };

  const handleInviaSdi = async (doc) => {
    const label = doc.tipo === 'nota_credito' ? 'nota di credito' : 'fattura';
    if (!window.confirm(`Inviare la ${label} ${doc.numero} al Sistema di Interscambio tramite Aruba?\n\nL'XML FatturaPA verrà generato e trasmesso automaticamente.`)) return;
    try {
      const res = await axios.post(`${API}/aruba/documenti/${doc.id}/invia`);
      toast.success(`✅ ${res.data.message} — file: ${res.data.upload_filename}`);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore invio a SdI'), { duration: 12000 });
    }
  };

  const handleSegnaSdi = async (doc, inviata) => {
    try {
      const res = await axios.post(`${API}/documenti/${doc.id}/segna-sdi?inviata=${inviata}`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore aggiornamento stato SdI'));
    }
  };

  const handleAggiornaSdi = async (id) => {
    try {
      const res = await axios.get(`${API}/aruba/documenti/${id}/stato`);
      toast.success(`Stato SdI: ${res.data.stato_sdi}`);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore aggiornamento stato SdI'), { duration: 10000 });
    }
  };

  const handleDownloadPDF = async (id, tipo, numero) => {
    try {
      const response = await axios.get(`${API}/documenti/${id}/pdf`, { responseType: 'blob' });
      downloadBlob(response.data, `${tipo}_${numero}.pdf`);
    } catch (error) {
      toast.error('Errore download PDF');
    }
  };

  const handlePreviewPDF = async (id) => {
    try {
      const response = await axios.get(`${API}/documenti/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Errore visualizzazione PDF');
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewOpen(false);
  };

  const handleSendEmail = async (doc) => {
    const cliente = clienti.find(c => c.id === doc.cliente_id);
    const defaultMail = cliente?.referente_email || cliente?.email || '';
    const dest = window.prompt(
      `Invia ${doc.tipo || 'preventivo'} al cliente via email\n\nIndirizzo destinatario:`,
      defaultMail
    );
    if (!dest) return;
    try {
      toast.loading('Invio email in corso...', { id: 'send-email' });
      const res = await axios.post(`${API}/documenti/${doc.id}/invia-email`, { destinatario: dest });
      toast.success(`Preventivo inviato a ${res.data.destinatario}!`, { id: 'send-email' });
    } catch (err) {
      toast.error(parseApiError(err, 'Errore invio email'), { id: 'send-email' });
    }
  };

  const handleSendEmailFattura = async (doc) => {
    const cliente = clienti.find(c => c.id === doc.cliente_id);
    const defaultMail = cliente?.referente_email || cliente?.email || '';
    const dest = window.prompt(
      `Invia fattura n. ${doc.numero} al cliente via email (PDF in allegato)\n\nIndirizzo destinatario:`,
      defaultMail
    );
    if (!dest) return;
    try {
      toast.loading('Invio fattura in corso...', { id: 'send-email-fattura' });
      const res = await axios.post(`${API}/documenti/${doc.id}/invia-email-fattura`, { destinatario: dest });
      toast.success(`Fattura inviata a ${res.data.destinatario}!`, { id: 'send-email-fattura' });
      fetchData();
    } catch (err) {
      toast.error(parseApiError(err, 'Errore invio email'), { id: 'send-email-fattura' });
    }
  };

  const handleSendWhatsApp = (doc) => {
    const cliente = clienti.find(c => c.id === doc.cliente_id);
    if (!cliente) {
      toast.error('Cliente non trovato');
      return;
    }
    // Numero utile: cellulare referente > telefono cliente
    const raw = cliente.referente_cellulare || cliente.telefono || '';
    let phone = (raw || '').replace(/[^\d+]/g, '');
    if (phone && !phone.startsWith('+')) {
      if (phone.startsWith('39')) phone = '+' + phone;
      else if (phone.startsWith('0')) phone = '+39' + phone.substring(1);
      else phone = '+39' + phone;
    }
    const portalUrl = `${window.location.origin}/p/preventivo/${doc.id}`;
    const anno = doc.created_at ? new Date(doc.created_at).getFullYear() : new Date().getFullYear();
    const tipo = (doc.tipo || 'preventivo').toUpperCase();
    const totale = (doc.totale || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    const messaggio = [
      `Buongiorno ${cliente.ragione_sociale || ''},`,
      ``,
      `Le inviamo il ${tipo} n. ${anno}/${String(doc.numero || '').padStart(3,'0')} per un importo di ${totale}.`,
      ``,
      `Puo accettarlo online in 30 secondi al seguente link:`,
      portalUrl,
      ``,
      `In alternativa puo scaricare il PDF, stamparlo, firmarlo e rispedirlo.`,
      ``,
      `Restiamo a disposizione per qualsiasi chiarimento.`,
      `Grazie.`
    ].join('\n');
    const waUrl = phone
      ? `https://wa.me/${phone.replace('+','')}?text=${encodeURIComponent(messaggio)}`
      : `https://wa.me/?text=${encodeURIComponent(messaggio)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    if (!phone) {
      toast.info('Numero cliente assente: scegli il destinatario in WhatsApp');
    }
  };

  const handleDownloadCertificato = async (id, numero) => {
    try {
      const response = await axios.get(`${API}/documenti/${id}/certificato-accettazione/pdf`, { responseType: 'blob' });
      downloadBlob(response.data, `certificato_accettazione_${numero}.pdf`);
      toast.success('Certificato di accettazione scaricato');
    } catch (error) {
      toast.error('Errore download certificato');
    }
  };

  const handleAccettazioneCartacea = async (doc, annulla) => {
    const label = `${DOCUMENTO_LABELS[doc.tipo] || doc.tipo} ${doc.numero}`;
    const msg = annulla
      ? `Annullare l'accettazione con firma cartacea di ${label}?`
      : `Confermi che ${label} è stato accettato dal cliente con FIRMA CARTACEA?\n\nPotrai generare fattura e DDT normalmente, senza registrare l'accettazione digitale.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API}/documenti/${doc.id}/accettazione-cartacea`, annulla ? { annulla: true } : {});
      toast.success(res.data.messaggio);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore accettazione cartacea'));
    }
  };

  const eseguiConversione = async (doc, tipoDest, dataDocumento) => {
    const body = { tipo_destinazione: tipoDest };
    if (dataDocumento) body.data_documento = dataDocumento;
    try {
      const res = await axios.post(`${API}/documenti/${doc.id}/converti`, body);
      toast.success(res.data.messaggio || 'Documento creato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore conversione'), { duration: 10000 });
    }
  };

  const handleConverti = (doc, tipoDest) => {
    if (tipoDest === 'fattura') {
      const dataPrev = String(doc.data_documento || doc.created_at || '').slice(0, 10);
      const oggi = new Date().toISOString().slice(0, 10);
      if (dataPrev && dataPrev !== oggi) {
        setDataFatturaDialog({ doc, dataPrev, oggi });
        return;
      }
    }
    eseguiConversione(doc, tipoDest, null);
  };

  // ---- Filtri e ordinamento ----
  const filteredDocumenti = documenti.filter(d => {
    if (!tipiVisibili.includes(d.tipo)) return false;
    if (soloNonPagate && !(d.tipo === 'fattura' && !d.dati_fattura?.pagato)) return false;
    const matchSearch = !filtri.search ||
      d.numero?.toString().includes(filtri.search) ||
      d.oggetto?.toLowerCase().includes(filtri.search.toLowerCase()) ||
      getClienteName(d.cliente_id)?.toLowerCase().includes(filtri.search.toLowerCase());
    const matchTipo = !filtri.tipo || d.tipo === filtri.tipo;
    let matchStato = true;
    if (filtri.stato) {
      switch (filtri.stato) {
        case 'bozza': matchStato = !!d.bozza; break;
        case 'in_attesa': matchStato = !d.bozza && (d.tipo === 'preventivo' || d.tipo === 'offerta') && !d.stato_accettazione; break;
        case 'accettato': matchStato = d.stato_accettazione === 'accettato'; break;
        case 'rifiutato': matchStato = d.stato_accettazione === 'rifiutato'; break;
        case 'pagata': matchStato = d.tipo === 'fattura' && !!d.dati_fattura?.pagato; break;
        case 'non_pagata': matchStato = !d.bozza && d.tipo === 'fattura' && !d.dati_fattura?.pagato; break;
        default: matchStato = true;
      }
    }
    const matchCliente = !filtri.cliente || d.cliente_id === filtri.cliente;
    const matchCreatoDa = !filtri.creatoDa || d.created_by === filtri.creatoDa;
    let matchData = true;
    if (filtri.dataDa || filtri.dataA) {
      const docDate = new Date(d.created_at);
      if (filtri.dataDa) {
        const dataDa = new Date(filtri.dataDa);
        dataDa.setHours(0, 0, 0, 0);
        if (docDate < dataDa) matchData = false;
      }
      if (filtri.dataA) {
        const dataA = new Date(filtri.dataA);
        dataA.setHours(23, 59, 59, 999);
        if (docDate > dataA) matchData = false;
      }
    }
    return matchSearch && matchTipo && matchStato && matchCliente && matchCreatoDa && matchData;
  });

  const sortedDocumenti = [...filteredDocumenti].sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'numero':
        cmp = (a.numero || 0) - (b.numero || 0);
        break;
      case 'totale':
        cmp = (a.totale || 0) - (b.totale || 0);
        break;
      case 'cliente':
        cmp = (getClienteName(a.cliente_id) || '').localeCompare(getClienteName(b.cliente_id) || '');
        break;
      case 'stato': {
        // ordine: accettato → visualizzato → in attesa → null
        const score = (d) => {
          if (d.stato_accettazione === 'accettato') return 3;
          if (d.prima_apertura_portale) return 2;
          if (!d.stato_accettazione) return 1;
          return 0;
        };
        cmp = score(a) - score(b);
        break;
      }
      case 'data':
      default:
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortDir === 'desc' ? -cmp : cmp;
  });

  const hasActiveFiltri = Object.values(filtri).some(Boolean);

  // ---- Paginazione ----
  const PER_PAGE = 20;
  const totalPages = Math.max(1, Math.ceil(sortedDocumenti.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pagedDocumenti = sortedDocumenti.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documenti-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Documenti</h1>
          <p className="text-muted-foreground text-sm mt-1">Preventivi, Offerte, DDT e Fatture</p>
        </div>
        <Button className="rounded-md font-mono uppercase tracking-wider" onClick={handleNuovo} data-testid="new-documento-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Documento
        </Button>
      </div>

      <DocumentiFilters
        filtri={filtri}
        setFiltri={setFiltri}
        resetFiltri={() => setFiltri(FILTRI_VUOTI)}
        hasActiveFiltri={hasActiveFiltri}
        tipiVisibili={tipiVisibili}
        clienti={clienti}
        utenti={utenti}
        documenti={documenti}
        isAdmin={isAdmin()}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
        risultati={sortedDocumenti.length}
      />

      {/* Stats Cards - Solo tipi visibili */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(DOCUMENTO_LABELS)
          .filter(([tipo]) => tipiVisibili.includes(tipo))
          .map(([tipo, { label, color, icon: Icon }]) => {
            const count = documenti.filter(d => d.tipo === tipo).length;
            return (
              <Card
                key={tipo}
                className={`rounded-md cursor-pointer transition-all hover:shadow-md ${filtri.tipo === tipo ? 'ring-2 ring-cyan-500' : ''}`}
                onClick={() => setFiltri(prev => ({ ...prev, tipo: prev.tipo === tipo ? '' : tipo }))}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-md ${color} flex items-center justify-center`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-black">{count}</p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        {tipiVisibili.includes('fattura') && (() => {
          const nonPagate = documenti.filter(d => d.tipo === 'fattura' && !d.dati_fattura?.pagato);
          const totale = nonPagate.reduce((sum, d) => sum + (d.importo_totale || 0), 0);
          return (
            <Card
              className={`rounded-md cursor-pointer transition-all hover:shadow-md border-red-300 dark:border-red-900 ${soloNonPagate ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => setSoloNonPagate(prev => !prev)}
              title={soloNonPagate ? 'Clicca per mostrare tutti i documenti' : 'Clicca per vedere SOLO le fatture non pagate'}
              data-testid="card-fatture-non-pagate"
            >
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-red-500 flex items-center justify-center">
                  <Euro className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-black text-red-600" data-testid="count-fatture-non-pagate">{nonPagate.length}</p>
                  <p className="text-xs text-muted-foreground truncate">Da incassare · € {totale.toFixed(2)}</p>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {soloNonPagate && (
        <div className="flex items-center gap-2 text-sm bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-md px-3 py-2" data-testid="banner-non-pagate">
          <Euro className="h-4 w-4 text-red-600 shrink-0" />
          <span>Stai vedendo <strong>solo le fatture non pagate</strong> ({sortedDocumenti.length}).</span>
          <button className="ml-auto text-red-600 hover:underline font-medium" onClick={() => setSoloNonPagate(false)} data-testid="reset-non-pagate">
            Mostra tutto
          </button>
        </div>
      )}

      <DocumentiTable
        documenti={pagedDocumenti}
        getClienteName={getClienteName}
        isAdmin={isAdmin()}
        canEditDoc={canEditDoc}
        canConverti={isAdmin() || !!permessiDocumenti.fattura || !!permessiDocumenti.ddt}
        handlers={{
          onPreview: handlePreviewPDF,
          onDownloadPDF: handleDownloadPDF,
          onDownloadXML: handleDownloadXML,
          onInviaSdi: handleInviaSdi,
          onAggiornaSdi: handleAggiornaSdi,
          onSegnaSdi: handleSegnaSdi,
          onSendEmail: handleSendEmail,
          onSendEmailFattura: handleSendEmailFattura,
          onSendWhatsApp: handleSendWhatsApp,
          onDownloadCertificato: handleDownloadCertificato,
          onAccettazioneCartacea: handleAccettazioneCartacea,
          onConverti: handleConverti,
          onTogglePagato: handleTogglePagato,
          onNotaCredito: handleNotaCredito,
          onEdit: handleEdit,
          onDelete: handleDelete,
        }}
      />

      {sortedDocumenti.length > PER_PAGE && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1" data-testid="documenti-paginazione">
          <span className="text-xs text-muted-foreground">
            Pagina <strong>{safePage}</strong> di <strong>{totalPages}</strong> — {sortedDocumenti.length} documenti
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="rounded-md h-8 px-2 text-xs" disabled={safePage === 1} onClick={() => setPage(safePage - 1)} data-testid="pagina-prec">
              ← Prec
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
              .map((n, idx, arr) => (
                <span key={n} className="flex items-center">
                  {idx > 0 && arr[idx - 1] !== n - 1 && <span className="px-1 text-xs text-muted-foreground">…</span>}
                  <Button
                    variant={n === safePage ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-md h-8 w-8 p-0 text-xs"
                    onClick={() => setPage(n)}
                    data-testid={`pagina-${n}`}
                  >
                    {n}
                  </Button>
                </span>
              ))}
            <Button variant="outline" size="sm" className="rounded-md h-8 px-2 text-xs" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)} data-testid="pagina-succ">
              Succ →
            </Button>
          </div>
        </div>
      )}

      <DocumentoFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingDocumento={editingDocumento}
        clienti={clienti}
        tipiVisibili={tipiVisibili}
        onSaved={fetchData}
      />

      <PdfPreviewDialog open={previewOpen} url={previewUrl} onClose={closePreview} />

      {/* Scelta data fattura quando la data del preventivo è diversa da oggi */}
      <Dialog open={!!dataFatturaDialog} onOpenChange={(open) => { if (!open) setDataFatturaDialog(null); }}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="data-fattura-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase tracking-wider text-base">Quale data vuoi usare per la fattura?</DialogTitle>
          </DialogHeader>
          {dataFatturaDialog && (() => {
            const fmt = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
            return (
              <div className="space-y-2 pt-1">
                <p className="text-sm text-muted-foreground">
                  Il preventivo n. {dataFatturaDialog.doc.numero} ha data {fmt(dataFatturaDialog.dataPrev)}, diversa da oggi.
                </p>
                <Button className="w-full rounded-none justify-start" data-testid="data-fattura-oggi"
                  onClick={() => { eseguiConversione(dataFatturaDialog.doc, 'fattura', null); setDataFatturaDialog(null); }}>
                  <Calendar className="h-4 w-4 mr-2" />Data di oggi ({fmt(dataFatturaDialog.oggi)})
                </Button>
                <Button variant="outline" className="w-full rounded-none justify-start" data-testid="data-fattura-preventivo"
                  onClick={() => { eseguiConversione(dataFatturaDialog.doc, 'fattura', dataFatturaDialog.dataPrev); setDataFatturaDialog(null); }}>
                  <Calendar className="h-4 w-4 mr-2" />Data del preventivo ({fmt(dataFatturaDialog.dataPrev)})
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
